import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyFormatters,
  dateFormatters,
  EMPTY_VALUE,
  numberFormatters,
} from '@asset-metrix/portal-ui';
import { Text } from '@mantine/core';

import { toDateString } from '../../utils/dateHelper';

import {
  DataSourceConfiguration,
  formatOperatorLabel,
  InvestmentComplianceLimitDataLevel,
  InvestmentLimitSourceColumn,
  isFilterOnlyLimit,
  LimitSetupDraft,
  needsDenominator,
} from './createInvestmentLimitModel';
import {
  aggregationMethodLabel,
  dataFieldLabel,
  expressionValueSuffix,
  isDataFieldValue,
  limitModeLabel,
  limitTypeLabel,
  valueTypeLabel,
} from './investmentLimitLabels';
import { DataLevelOptions, LimitSelectOption } from './investmentLimitTypes';
import { useInvestmentComplianceLimitOptions } from './useInvestmentComplianceLimitOptions';

import styles from './createInvestmentLimitModal.module.scss';

function formatReviewNumber(value: string, postfix?: string) {
  const trimmed = value.trim();
  const parsed = trimmed.length === 0 ? null : Number(trimmed);

  return (
    applyFormatters(
      numberFormatters,
      parsed != null && Number.isFinite(parsed) ? parsed : null,
      { postfix }
    ) ?? EMPTY_VALUE
  );
}

function formatReviewDate(date: Date | null) {
  if (date == null || Number.isNaN(date.getTime())) {
    return EMPTY_VALUE;
  }
  return applyFormatters(dateFormatters, toDateString(date), {}) ?? EMPTY_VALUE;
}

function formatFilterValues(
  values: string[],
  options: LimitSelectOption[] | undefined
) {
  if (values.length === 0) {
    return EMPTY_VALUE;
  }

  return values
    .map((value) => {
      const option = options?.find((item) => String(item.value) === value);
      return option?.label ?? value;
    })
    .join(', ');
}

function getFilterLines(
  source: DataSourceConfiguration,
  columns: InvestmentLimitSourceColumn[],
  filters: DataLevelOptions['filters']
) {
  return source.enabledFilterKeys.map((key) => {
    const label = columns.find((column) => column.key === key)?.label ?? key;
    const values = source.filterValues[key] ?? [];
    const options = filters.find((filter) => filter.key === key)?.options;
    return {
      key,
      text: `${label}: ${formatFilterValues(values, options)}`,
    };
  });
}

function ReviewFilterValue({
  lines,
}: {
  lines: Array<{ key: string; text: string }>;
}) {
  const { t } = useTranslation();

  if (lines.length === 0) {
    return t('app.investmentLimit.review.none');
  }

  return (
    <div className={styles['review-filter-list']}>
      {lines.map((line) => (
        <span key={line.key}>{line.text}</span>
      ))}
    </div>
  );
}

function ReviewItem({
  label,
  value,
  wide,
}: {
  label: string;
  value: ReactNode;
  wide?: boolean;
}) {
  return (
    <div
      className={`${styles['review-item']} ${
        wide ? styles['review-item-wide'] : ''
      }`}
    >
      <span className={styles['review-label']}>{label}</span>
      <div className={styles['review-value']}>{value}</div>
    </div>
  );
}

export type ReviewSummaryProps = {
  contentId: string;
  dataLevels: InvestmentComplianceLimitDataLevel[];
  draft: LimitSetupDraft;
};

export function ReviewSummary({
  contentId,
  dataLevels,
  draft,
}: ReviewSummaryProps) {
  const { t } = useTranslation();
  const parameter = draft.parameter;
  const parameterDataLevelIndex = dataLevels.findIndex(
    (level) => level.id === parameter.dataLevelId
  );
  const resolvedParameterDataLevelIndex =
    parameterDataLevelIndex === -1 ? null : parameterDataLevelIndex;
  const parameterDataLevel =
    resolvedParameterDataLevelIndex == null
      ? undefined
      : dataLevels[resolvedParameterDataLevelIndex];
  const denominatorDataLevelIndex = dataLevels.findIndex(
    (level) => level.id === parameter.denominator?.dataLevelId
  );
  const resolvedDenominatorDataLevelIndex =
    denominatorDataLevelIndex === -1 ? null : denominatorDataLevelIndex;
  const denominatorDataLevel =
    resolvedDenominatorDataLevelIndex == null
      ? undefined
      : dataLevels[resolvedDenominatorDataLevelIndex];
  const { data: parameterOptionsResponse } =
    useInvestmentComplianceLimitOptions(
      contentId,
      resolvedParameterDataLevelIndex
    );
  const { data: denominatorOptionsResponse } =
    useInvestmentComplianceLimitOptions(
      contentId,
      resolvedDenominatorDataLevelIndex
    );
  const parameterColumns = parameterDataLevel?.filters ?? [];
  const denominatorColumns = denominatorDataLevel?.filters ?? [];
  const parameterFilterOptions = parameterOptionsResponse?.data.filters ?? [];
  const denominatorFilterOptions =
    denominatorOptionsResponse?.data.filters ?? [];
  const limitValueLabel = formatReviewNumber(
    parameter.limitValue,
    expressionValueSuffix(draft.limitMode, parameter.valueType, 'limitValue')
  );
  const kpiLabel =
    parameter.dataField != null && isDataFieldValue(parameter.dataField)
      ? dataFieldLabel(t, parameter.dataField)
      : EMPTY_VALUE;
  const evaluationMetricLabel =
    parameter.evalMetric != null && isDataFieldValue(parameter.evalMetric)
      ? dataFieldLabel(t, parameter.evalMetric)
      : EMPTY_VALUE;
  const limitModeText = limitModeLabel(t, draft.limitMode);
  const kpiComparison = (
    <>
      <ReviewItem
        label={t('app.investmentLimit.review.kpi')}
        value={kpiLabel}
      />
      <ReviewItem
        label={t('app.investmentLimit.review.limitOperator')}
        value={formatOperatorLabel(parameter.ruleOperator) ?? EMPTY_VALUE}
      />
      <ReviewItem
        label={t('app.investmentLimit.review.limitValue')}
        value={limitValueLabel}
      />
    </>
  );
  const warningThreshold = (
    <ReviewItem
      label={t('app.investmentLimit.review.warningThreshold')}
      value={formatReviewNumber(parameter.warningThreshold, '%')}
    />
  );

  return (
    <>
      <Text className={styles['section-title']}>
        {t('app.investmentLimit.preview.overview')}
      </Text>
      <div className={styles['review-grid']}>
        <ReviewItem
          label={t('app.investmentLimit.review.limitName')}
          value={draft.limitName.trim() || EMPTY_VALUE}
        />
        <ReviewItem
          label={t('app.investmentLimit.review.limitType')}
          value={
            draft.limitType == null
              ? EMPTY_VALUE
              : limitTypeLabel(t, draft.limitType)
          }
        />
        <ReviewItem
          label={t('app.investmentLimit.review.limitMode')}
          value={limitModeText}
        />
        <ReviewItem
          label={t('app.investmentLimit.review.description')}
          value={draft.description.trim() || EMPTY_VALUE}
          wide
        />
        {!isFilterOnlyLimit(draft.limitMode) && (
          <ReviewItem
            label={t('app.investmentLimit.review.comparisonType')}
            value={valueTypeLabel(t, parameter.valueType)}
          />
        )}
      </div>
      <Text className={styles['section-title']}>
        {t('app.investmentLimit.preview.parameter')}
      </Text>
      <div className={styles['review-grid']}>
        <ReviewItem
          label={t('app.investmentLimit.review.dataLevel')}
          value={
            parameterDataLevel?.label ?? parameter.dataLevelId ?? EMPTY_VALUE
          }
        />
        <ReviewItem
          label={t('app.investmentLimit.review.currencyType')}
          value={parameter.currencyType ?? EMPTY_VALUE}
        />
        <ReviewItem
          label={t('app.investmentLimit.review.filters')}
          value={
            <ReviewFilterValue
              lines={getFilterLines(
                parameter,
                parameterColumns,
                parameterFilterOptions
              )}
            />
          }
          wide
        />
        {draft.limitMode === 'Aggregation' && (
          <>
            {kpiComparison}
            <ReviewItem
              label={t('app.investmentLimit.review.aggregationMethod')}
              value={
                parameter.aggregationMethod == null
                  ? EMPTY_VALUE
                  : aggregationMethodLabel(t, parameter.aggregationMethod)
              }
            />
            <ReviewItem
              label={t('app.investmentLimit.review.aggregationNumber')}
              value={formatReviewNumber(parameter.aggregationNumber)}
            />
            {warningThreshold}
          </>
        )}
        {draft.limitMode === 'Position' && (
          <>
            <ReviewItem
              label={t('app.investmentLimit.review.evaluationMetric')}
              value={evaluationMetricLabel}
            />
            <ReviewItem
              label={t('app.investmentLimit.review.evaluationOperator')}
              value={formatOperatorLabel(parameter.evalOperator) ?? EMPTY_VALUE}
            />
            <ReviewItem
              label={t('app.investmentLimit.review.evaluationValue')}
              value={formatReviewNumber(
                parameter.evalValue,
                expressionValueSuffix(
                  draft.limitMode,
                  parameter.valueType,
                  'evalValue'
                )
              )}
            />
            {kpiComparison}
            {warningThreshold}
          </>
        )}
      </div>
      {needsDenominator(draft) && parameter.denominator != null && (
        <>
          <Text className={styles['section-title']}>
            {t('app.investmentLimit.preview.denominator')}
          </Text>
          <div className={styles['review-grid']}>
            <ReviewItem
              label={t('app.investmentLimit.review.dataLevel')}
              value={
                denominatorDataLevel?.label ??
                parameter.denominator.dataLevelId ??
                EMPTY_VALUE
              }
            />
            <ReviewItem
              label={t('app.investmentLimit.review.currencyType')}
              value={parameter.denominator.currencyType ?? EMPTY_VALUE}
            />
            <ReviewItem
              label={t('app.investmentLimit.review.filters')}
              value={
                <ReviewFilterValue
                  lines={getFilterLines(
                    parameter.denominator,
                    denominatorColumns,
                    denominatorFilterOptions
                  )}
                />
              }
              wide
            />
          </div>
        </>
      )}
      <Text className={styles['section-title']}>
        {t('app.investmentLimit.preview.workflow')}
      </Text>
      <div className={styles['review-grid']}>
        <ReviewItem
          label={t('app.investmentLimit.review.breachApproval')}
          value={
            draft.breachWorkflowApproval
              ? t('app.investmentLimit.review.yes')
              : t('app.investmentLimit.review.no')
          }
        />
        <ReviewItem
          label={t('app.investmentLimit.review.validFrom')}
          value={formatReviewDate(draft.validFrom)}
        />
        <ReviewItem
          label={t('app.investmentLimit.review.validTo')}
          value={
            draft.validIndefinitely
              ? t('app.investmentLimit.review.indefinitely')
              : formatReviewDate(draft.validTo)
          }
        />
      </div>
    </>
  );
}
